// src/scripts/logout.js
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import router from "@/router";

let inactivityTimer = null;
let listenersAttached = false;
let isLoggingOut = false;
let authUnsub = null;

const TIME_LIMIT = 15 * 60 * 1000; // 15 minutes
const EVENTS = ["click", "keydown", "mousemove", "scroll", "touchstart"];
const LISTENER_OPTS = { passive: true, capture: false };

export async function logout() {
  if (isLoggingOut) return;
  isLoggingOut = true;

  // Stop timer + detach listeners immediately
  stopInactivityTimer();

  // Close Materialize sidenav if open (prevents teardown glitches)
  try {
    const el = document.querySelector("#mobile-nav");
    const inst = window.M?.Sidenav?.getInstance?.(el);
    inst?.close?.();
  } catch (_) {
    // ignore
  }

  try {
    const auth = getAuth();
    await signOut(auth);

    // Avoid redundant navigation
    if (router.currentRoute.value?.name !== "Login") {
      await router.replace({ name: "Login" });
    }
  } catch (error) {
    console.error("Logout failed:", error);
  } finally {
    isLoggingOut = false;
  }
}

/**
 * Initialize inactivity timer globally.
 * Binds start/stop to Firebase auth state so it works after refresh.
 * Call ONCE from main.js (recommended after router.isReady()).
 */
export function initInactivityTimer() {
  if (typeof window === "undefined") return;
  if (authUnsub) return; // already initialized

  const auth = getAuth();

  authUnsub = onAuthStateChanged(auth, (user) => {
    if (user) {
      startInactivityTimer();
    } else {
      stopInactivityTimer();
    }
  });
}

export function destroyInactivityTimer() {
  authUnsub?.();
  authUnsub = null;
  stopInactivityTimer();
}

export function startInactivityTimer() {
  if (typeof window === "undefined") return;
  if (isLoggingOut) return;

  const auth = getAuth();
  if (!auth.currentUser) return; // only run when logged in

  if (!listenersAttached) {
    EVENTS.forEach((event) => {
      window.addEventListener(event, resetTimer, LISTENER_OPTS);
    });
    listenersAttached = true;
  }

  resetTimer();
}

export function stopInactivityTimer() {
  if (typeof window === "undefined") return;

  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }

  if (listenersAttached) {
    EVENTS.forEach((event) => {
      window.removeEventListener(event, resetTimer, LISTENER_OPTS);
    });
    listenersAttached = false;
  }
}

function resetTimer() {
  if (isLoggingOut) return;

  const auth = getAuth();
  if (!auth.currentUser) return; // don't arm timers when logged out

  if (inactivityTimer) clearTimeout(inactivityTimer);

  inactivityTimer = setTimeout(() => {
    logout();
  }, TIME_LIMIT);
}
